import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, ChevronLeft, ChevronRight, Clock, Cpu, Layers, Zap } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { StatCard } from '../components/common/StatCard';
import { getBlock, getBlockTransactions } from '../lib/api';
import { formatNumber, formatDate, formatBytes, timeAgo, formatGG } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

export default function BlockDetail() {
  const { identifier } = useParams();
  const [block, setBlock] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const blockRes = await getBlock(identifier);
        setBlock(blockRes.data);
        
        const txRes = await getBlockTransactions(blockRes.data.height);
        setTransactions(txRes.data);
      } catch (err) {
        setError('Block not found');
        console.error('Error fetching block:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [identifier]);

  const txColumns = [
    {
      header: 'Hash',
      cell: (row) => <HashDisplay hash={row.hash} link={`/tx/${row.hash}`} />,
    },
    {
      header: 'From',
      cell: (row) => <HashDisplay hash={row.from_address} type="address" link={`/address/${row.from_address}`} />,
    },
    {
      header: 'To',
      cell: (row) => <HashDisplay hash={row.to_address} type="address" link={`/address/${row.to_address}`} />,
    },
    {
      header: 'Value',
      cell: (row) => <span className="text-primary font-mono">{formatNumber(row.value, 6)} GG</span>,
    },
    {
      header: 'Fee',
      cell: (row) => <span className="text-muted-foreground font-mono text-sm">{row.fee.toFixed(6)} GG</span>,
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 text-center">
          <div className="data-card p-12">
            <Box className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Block Not Found</h2>
            <p className="text-muted-foreground mb-4">The requested block could not be found.</p>
            <Link to="/blocks">
              <Button>View All Blocks</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="block-detail-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
              <Box className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">
                Block #{formatNumber(block.height, 0)}
              </h1>
              <p className="text-sm text-muted-foreground">{timeAgo(block.timestamp)}</p>
            </div>
          </div>
          
          {/* Navigation */}
          <div className="flex items-center gap-2">
            {block.height > 1 && (
              <Link to={`/block/${block.height - 1}`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
              </Link>
            )}
            <Link to={`/block/${block.height + 1}`}>
              <Button variant="outline" size="sm" className="gap-2">
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Transactions"
            value={block.tx_count}
            icon={Layers}
            accentColor="primary"
          />
          <StatCard
            title="Block Reward"
            value={`${block.reward.total.toFixed(6)} GG`}
            subtitle={`PoW: ${block.reward.pow.toFixed(4)} | PoS: ${block.reward.pos.toFixed(4)}`}
            icon={Zap}
            accentColor="success"
          />
          <StatCard
            title="Size"
            value={formatBytes(block.size)}
            icon={Box}
            accentColor="secondary"
          />
          <StatCard
            title="Gas Used"
            value={`${formatNumber((block.gas_used / block.gas_limit) * 100, 1)}%`}
            subtitle={`${formatNumber(block.gas_used, 0)} / ${formatNumber(block.gas_limit, 0)}`}
            icon={Cpu}
            accentColor="warning"
          />
        </div>

        {/* Block Details */}
        <div className="data-card p-6 mb-8" data-testid="block-details">
          <h2 className="text-lg font-bold mb-4">Block Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Block Hash</p>
                <HashDisplay hash={block.hash} truncate={false} className="break-all" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Parent Hash</p>
                <HashDisplay 
                  hash={block.previous_hash} 
                  link={block.height > 1 ? `/block/${block.height - 1}` : undefined}
                  truncate={false} 
                  className="break-all" 
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Merkle Root</p>
                <HashDisplay hash={block.merkle_root} truncate={false} className="break-all" />
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Miner</p>
                <HashDisplay hash={block.miner} type="address" link={`/address/${block.miner}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Validator</p>
                <HashDisplay hash={block.validator} type="address" link={`/address/${block.validator}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Timestamp</p>
                <p className="font-mono text-sm">{formatDate(block.timestamp)}</p>
              </div>
              <div className="flex gap-8">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Difficulty</p>
                  <p className="font-mono">{formatNumber(block.difficulty, 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Nonce</p>
                  <p className="font-mono">{formatNumber(block.nonce, 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Epoch</p>
                  <p className="font-mono">{block.reward.epoch}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div>
          <h2 className="text-lg font-bold mb-4">Transactions ({transactions.length})</h2>
          <DataTable
            columns={txColumns}
            data={transactions}
            loading={false}
            emptyMessage="No transactions in this block"
          />
        </div>
      </div>
    </Layout>
  );
}
